import { action } from '@reatom/framework'
import { match } from 'ts-pattern'

import { identity, pipe, Result } from '@blade/std-lib'
import { currencyDtoUpdatedEvent } from '@root/entities/currency'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { premiumMetaUpdateReceived } from '@root/entities/premium'
import { Socials } from '@root/entities/socials'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { DomUtil } from '@root/shared'
import { ApiError, apiRequest } from '@root/shared/api'
import {
  FastifyErrorCode,
  PremiumErrorCode,
} from '@root/shared/api/api-error-code'
import { reatomRequest } from '@root/shared/lib'

import type { ActionPayload } from '@reatom/framework'
import type { CurrencyAddress } from '@root/entities/currency/model/CurrencyAddress'
import type { PremiumId } from '@root/entities/premium'
import type { ServicesApi } from '@root/shared/api'

export const updatePremiumCurrencyRequestAction = action(
  async (
    ctx,
    {
      logoBase64,
      bannerBase64,
      premiumId,
      customEmojiId,
      network,
      socials,
    }: {
      logoBase64?: string
      bannerBase64?: string
      customEmojiId?: string
      premiumId: PremiumId
      currencyAddress: CurrencyAddress
      network: Network.Network
      socials?: Socials.Socials
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.premium.premiumUpdateTokenCreate, [
        {
          premiumId: premiumId,
          logo: logoBase64,
          banner: bannerBase64,
          trendingEmojiId: customEmojiId,
          network: Network.toDto(network),
          socials: socials ? Socials.toDto(socials) : undefined,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError((e) =>
        match(e)
          .when(
            ApiError.isOfCodes([
              PremiumErrorCode.PremiumNotFound,
              PremiumErrorCode.ImageContentNotSafe,
              PremiumErrorCode.ImageSafetyCheckFailed,
              FastifyErrorCode.ContentBodyTooLarge,
            ]),
            identity,
          )
          .otherwise(() => e),
      ),
      Result.map(
        (
          it,
        ): {
          currency: ServicesApi.TokenMetaDto
          premium: ServicesApi.PremiumMetaDto
        } => ({
          currency: it,
          premium: it.premium,
        }),
      ),
    )

    if (Result.isSuccess(mappedResult)) {
      premiumMetaUpdateReceived(ctx, {
        currency: mappedResult.success.currency,
        premiumMeta: mappedResult.success.premium,
      })
    }

    return mappedResult
  },
  'updatePremiumCurrencyRequestAction',
)

type UpdateResult = Awaited<
  ActionPayload<typeof updatePremiumCurrencyRequestAction>
>

export type UpdatePremiumCurrencyRequestError =
  Result.Result.Failure<UpdateResult>

export const reatomUpdatePremiumCurrency = () => {
  const processAction = action(
    async (
      ctx,
      {
        logo,
        banner,
        premiumId,
        emojiId,
        currencyAddress,
      }: {
        logo?: File
        banner?: File
        emojiId?: string
        premiumId: PremiumId
        currencyAddress: CurrencyAddress
      },
    ) => {
      let logoToUpdate = undefined
      if (logo) {
        const base64Result = await ctx.schedule(() =>
          DomUtil.blobToBase64(logo),
        )
        if (Result.isFailure(base64Result)) {
          return Result.fail(base64Result.failure)
        }
        logoToUpdate = base64Result.success
      }

      let bannerToUpdate = undefined
      if (banner) {
        const base64Result = await ctx.schedule(() =>
          DomUtil.blobToBase64(banner),
        )
        if (Result.isFailure(base64Result)) {
          return Result.fail(base64Result.failure)
        }
        bannerToUpdate = base64Result.success
      }

      const network = ctx.get(selectedNetworkAtom)

      const result = await ctx.schedule(() =>
        updatePremiumCurrencyRequestAction(ctx, {
          logoBase64: logoToUpdate,
          bannerBase64: bannerToUpdate,
          premiumId: premiumId,
          customEmojiId: emojiId,
          currencyAddress,
          network,
        }),
      )

      if (Result.isSuccess(result)) {
        currencyDtoUpdatedEvent(ctx, result.success.currency)
        premiumMetaUpdateReceived(ctx, {
          currency: result.success.currency,
          premiumMeta: result.success.premium,
        })
      }

      return result
    },
    'processAction',
  )

  const { executeAction, requestStateAtom, resetStateAction } = reatomRequest({
    execAction: processAction,
    name: `updatePremium`,
  })

  return {
    updateRequestState: requestStateAtom,
    updateCurrencyAction: executeAction,
    resetStateAction,
  }
}
